import React, { useCallback, useEffect, useRef, useState } from 'react';

import dayjs from 'dayjs';

import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { storeSetAuth } from '@store/auth-reducer';
import { CONST_COMMON, CONST_CHAT_MANAGEMENT } from 'constants/language';
import { useAppSelector } from '@hooks/useSelector/useAppSelector';
import { responseSuccess } from 'helper/responseSuccess/responseSuccess';
import { MenuProps, theme } from 'antd';
import { IFootstepsHistory } from '@pages/OperatorSite/FootstepsHistory/useFootstepsHistory';
import { getEmailFirebase, getPasswordFirebase } from 'helper/firebase';
import { collection, limit, onSnapshot, orderBy, query, deleteDoc, doc } from 'firebase/firestore';
import { firestoreFirebaseApp, firestoreFirebaseAuthApp, firebaseMessaging } from '@hooks/useFireBase/useFireBase';
import { apiOperatorFootstepsHistory, getCompanyCountChatMessagesNotRead, apiSaveWebFCMToken, apiDeleteWebFCMToken } from 'api/operator';

const useHeaderOperator = ({ onHandleLogout }: any) => {
  const [listNoticeCurrent, setListNoticeCurrent] = useState<string[]>([]);
  const timeComponentRender = useRef<Date>(new Date());

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    token: { colorBgContainer },
  } = theme.useToken();

  const items: MenuProps['items'] = [
    {
      key: '1',
      label: <div onClick={onHandleLogout}>{CONST_COMMON.PROFILE_POPUP.LOGOUT}</div>,
    },
  ];

  const firestoreFirebase = firestoreFirebaseApp;
  const firestoreFirebaseAuth = firestoreFirebaseAuthApp;
  const fcmMessaging = firebaseMessaging;
  const authInfo = useAppSelector((state) => state.auth.authInfo);
  const fcmTokenRef = useRef<string | null>(null);
  const [messagesNotRead, setMessagesNotRead] = useState<number>(0);
  const [messagesMysqlNotRead, setMessagesMysqlNotRead] = useState<number>(0);

  const [messagesNotReadShow, setMessagesNotReadShow] = useState<number>(0);

  const getListHistory = async () => {
    try {
      const response = await apiOperatorFootstepsHistory({
        date: dayjs().format('YYYY-MM'),
        page: 1,
        per_page: 5,
      });
      if (responseSuccess(response)) {
        setListNoticeCurrent(
          response?.data?.data[0].slice(0, 5).map((element: IFootstepsHistory) => element.description)
        );
      }
    } catch (errors) { }
  };

  useEffect(() => {
    getListHistory();
  }, []);

  // count total mesasge mysql not read
  const getTotalMessageNotRead = async () => {
    const response = await getCompanyCountChatMessagesNotRead();
    if (responseSuccess(response)) {
      setMessagesMysqlNotRead(response.data.count);
    }
  };

  const handleClickChat = () => {
    getTotalMessageNotRead();
    navigate('/chat');
  };

  // get messages not read
  const getMessages = useCallback(() => {
    if (!authInfo || !firestoreFirebaseAuth?.currentUser || !firestoreFirebase) return;

    const messagesRef = collection(firestoreFirebase, 'messages');
    const queryGetListMessage = query(messagesRef);

    onSnapshot(queryGetListMessage, (messagesSnapshot: any) => {
      let countMessage = 0;
      messagesSnapshot.docs.forEach((doc: any) => {
        const messageData = doc.data();
        if (!messageData.reads?.includes(`${authInfo.user.user_role}.${authInfo.user.id}`)) {
          countMessage++;
        }
      });

      setMessagesNotRead(messagesMysqlNotRead + countMessage);
    });
  }, [authInfo, firestoreFirebase, messagesMysqlNotRead, firestoreFirebaseAuth?.currentUser]);

  const registerFCMToken = useCallback(() => {
    if (!fcmMessaging || !authInfo?.user) return;
    const vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY;
    if (!vapidKey) return;

    navigator.serviceWorker
      .register('/firebase-messaging-sw.js')
      .then((registration) => {
        // Wait until SW is fully activated before calling getToken
        if (registration.active?.state === 'activated') return registration;
        return new Promise<ServiceWorkerRegistration>((resolve) => {
          const sw = registration.installing || registration.waiting || registration.active;
          if (!sw) { resolve(registration); return; }
          sw.addEventListener('statechange', () => {
            if (sw.state === 'activated') resolve(registration);
          });
        });
      })
      .then((registration) => {
        return fcmMessaging.getToken({ vapidKey, serviceWorkerRegistration: registration })
          .catch((err) => {
            // If a push subscription with a different VAPID key exists, unsubscribe and retry
            if (err?.name === 'AbortError' || err?.name === 'InvalidStateError') {
              return registration.pushManager.getSubscription()
                .then((sub) => sub?.unsubscribe())
                .then(() => fcmMessaging.getToken({ vapidKey, serviceWorkerRegistration: registration }));
            }
            throw err;
          });
      })
      .then((token) => {
        if (token && token !== fcmTokenRef.current) {
          fcmTokenRef.current = token;
          apiSaveWebFCMToken(token).catch(() => { });
        }
      })
      .catch(() => { });
  }, [fcmMessaging, authInfo?.user]); // eslint-disable-line

  // Handle FCM foreground messages — show desktop notification when tab is open
  useEffect(() => {
    if (!fcmMessaging) return;
    const unsubscribe = fcmMessaging.onMessage((payload: any) => {
      if ('Notification' in window && Notification.permission === 'granted') {
        const title = payload.data?.title || CONST_CHAT_MANAGEMENT.NOTIFICATION_TITLE;
        const body = payload.data?.body || '';
        const link = payload.data?.link || '/chat';
        const notification = new Notification(title, {
          body,
          icon: '/logo.svg',
        });
        notification.onclick = () => {
          window.focus();
          navigate(link);
          notification.close();
        };
      }
    });
    return () => unsubscribe();
    // eslint-disable-next-line
  }, [fcmMessaging]);

  // Register FCM token on mount if permission already granted,
  // or when the chat page banner fires 'notification-permission-granted'
  useEffect(() => {
    if (Notification.permission === 'granted') {
      registerFCMToken();
    }
    const handler = () => registerFCMToken();
    window.addEventListener('notification-permission-granted', handler);
    return () => window.removeEventListener('notification-permission-granted', handler);
    // eslint-disable-next-line
  }, [authInfo?.user, fcmMessaging]);

  // Delete FCM token on logout / component unmount
  useEffect(() => {
    return () => {
      if (fcmTokenRef.current) {
        apiDeleteWebFCMToken(fcmTokenRef.current).catch(() => { });
      }
    };
    // eslint-disable-next-line
  }, []);

  // listen for web_operator_notifications and show desktop notification
  const listenWebNotifications = useCallback(() => {
    if (!authInfo || !firestoreFirebaseAuth?.currentUser || !firestoreFirebase) return;

    const notificationsRef = collection(firestoreFirebase, 'web_operator_notifications');
    const queryNotifications = query(notificationsRef, orderBy('created_at', 'desc'));

    return onSnapshot(
      queryNotifications,
      (snapshot: any) => {
        snapshot.docChanges().forEach((change: any) => {
          if (change.type === 'added') {
            const data = change.doc.data();

            // filter: only documents created after component mount
            const docCreatedAt = data?.created_at?.seconds
              ? new Date(data.created_at.seconds * 1000)
              : null;
            if (!docCreatedAt || docCreatedAt < timeComponentRender.current) return;

            // show browser desktop notification
            if ('Notification' in window && Notification.permission === 'granted') {
              const notification = new Notification(CONST_CHAT_MANAGEMENT.NOTIFICATION_TITLE, {
                body: `${CONST_CHAT_MANAGEMENT.NOTIFICATION_BODY_COMPANY}${data.company_name || ''}\n${data.content || ''}`,
                icon: '/logo.svg',
                tag: `chat-${change.doc.id}`,
              });

              notification.onclick = () => {
                window.focus();
                if (data.group_company_id) {
                  navigate(`/chat/${data.group_company_id}`);
                } else {
                  navigate('/chat');
                }
                notification.close();
              };
            }

            // delete the notification document after showing
            deleteDoc(doc(firestoreFirebase, 'web_operator_notifications', change.doc.id)).catch(() => { });
          }
        });
      },
      (error: any) => {
        console.error('[web_operator_notifications] Firestore listener error:', error);
      }
    );
  }, [authInfo, firestoreFirebase, firestoreFirebaseAuth?.currentUser, navigate]);

  useEffect(() => {
    const unsubscribe = listenWebNotifications();
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [listenWebNotifications]);

  // get new notice
  const getNotice = useCallback(() => {
    if (!authInfo || !firestoreFirebaseAuth?.currentUser || !firestoreFirebase) return;

    const noticeRef = collection(firestoreFirebase, 'notice');
    const queryGetNotice = query(noticeRef, orderBy('created_at', 'desc'), limit(1));

    return onSnapshot(queryGetNotice, (noticeSnapshot: any) => {
      noticeSnapshot.docs.forEach((doc: any) => {
        const noticeData = doc.data();
        const noticeCreatedAt = new Date(noticeData?.created_at?.seconds * 1000);

        if (
          noticeData?.description &&
          noticeData?.created_at &&
          noticeCreatedAt.getTime() >= timeComponentRender.current.getTime()
        ) {
          getListHistory();
        }
      });
    });
  }, [authInfo, firestoreFirebase, firestoreFirebaseAuth?.currentUser]);

  useEffect(() => {
    getMessages();

    return () => {
      getMessages();
    };
  }, [getMessages]);

  useEffect(() => {
    const unsubscribe = getNotice();

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [getNotice]);

  // refesh token
  useEffect(() => {
    if (!firestoreFirebaseAuth?.currentUser && authInfo?.user) {
      firestoreFirebaseAuth?.signOut();
      const emailUser = getEmailFirebase(authInfo.user?.email, authInfo.user?.user_role);
      const passwordUser = getPasswordFirebase(authInfo.user?.email, authInfo.user?.user_role);
      if (emailUser && passwordUser) {
        firestoreFirebaseAuth?.signInWithEmailAndPassword(emailUser, passwordUser);
      }
    }
  }, [firestoreFirebaseAuth, authInfo]);

  useEffect(() => {
    dispatch(
      storeSetAuth({
        ...authInfo,
        messagesNotRead,
      })
    );
    // eslint-disable-next-line
  }, [messagesNotRead, dispatch]);

  useEffect(() => {
    setMessagesNotReadShow(authInfo?.messagesNotRead);
    // eslint-disable-next-line
  }, [authInfo?.messagesNotRead]);

  useEffect(() => {
    getTotalMessageNotRead();
  }, []);

  useEffect(() => {
    const intervalId = setInterval(() => {
      if (listNoticeCurrent.length !== 0) {
        let temp: any[] = [...listNoticeCurrent];
        if (temp && temp.length > 0) {
          temp.push(temp.shift());
        }
        setListNoticeCurrent(temp || []);
      }
    }, 13000);
    return () => {
      clearInterval(intervalId);
    };
  }, [listNoticeCurrent]);

  return {
    handleClickChat,
    messagesNotReadShow,
    colorBgContainer,
    items,
    onHandleLogout,
    listNoticeCurrent,
  };
};

export default useHeaderOperator;
